/**
 * Life4Earth V2 — Project Journal API adapter.
 *
 * The Journal is a curated, time-ordered post stream on mini-projects AND full
 * projects (polymorphic via entityType). Epic DEV-1295 / Phase A DEV-1296.
 * See SPEC_PROJECT_JOURNAL.md.
 *
 * Thin vertical (this slice): native posts — list + create. Reference sources
 * (link/chronicle/topic), media, review workflow, and status-origin posts
 * layer on next.
 *
 *   GET  /api/journal/{entityType}/{entityGuid}/posts
 *   POST /api/journal/{entityType}/{entityGuid}/posts
 */

import { apiFetch } from './client';

export type JournalEntityType = 'mini_project' | 'project';

/** The content type — the only discriminator. The Journal is a share feed. */
export type JournalSource =
  | 'status'
  | 'link'
  | 'topic_tracker'
  | 'project_chronicle'
  | 'mini_project'
  | 'news'
  | 'lessons_learned'
  | 'best_practices'
  | 'scholarly';

export interface JournalPostMedia {
  mediaGuid: string;
  mediaType: 'photo' | 'video';
  storageKey: string;
  caption?: string | null;
  position: number;
}

export interface JournalPost {
  journalPostGuid: string;
  entityType: JournalEntityType;
  entityGuid: string;
  source: JournalSource;
  title?: string | null;
  narrativeHtml?: string | null;
  sourceUrl?: string | null;
  ogTitle?: string | null;
  ogDescription?: string | null;
  ogImageUrl?: string | null;
  ogSiteName?: string | null;
  chronicleGuid?: string | null;
  topicArticleGuid?: string | null;
  statusGuid?: string | null;
  statusSignal?: string | null;
  reviewStatus: 'draft' | 'pending_review' | 'published';
  communityVisible: boolean;
  postedByUserGuid: string;
  postedAt: string;
  updatedAt: string;
  media: JournalPostMedia[];
}

export interface CreateJournalPostBody {
  /** Content type being shared. This slice: 'link' | 'project_chronicle'. */
  source: JournalSource;
  title?: string | null;
  /** source='link': the article/video URL. */
  sourceUrl?: string | null;
  /** Optional "why I'm sharing this" context. */
  comment?: string | null;
  /** Cached OG/oEmbed preview (from FeedShareModal), so we needn't re-scrape. */
  ogTitle?: string | null;
  ogDescription?: string | null;
  ogImageUrl?: string | null;
  ogSiteName?: string | null;
  chronicleGuid?: string | null;
  topicArticleGuid?: string | null;
  communityVisible?: boolean;
}

export async function fetchJournalPosts(
  entityType: JournalEntityType,
  entityGuid: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<JournalPost[]> {
  const qs = new URLSearchParams();
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  if (opts.offset != null) qs.set('offset', String(opts.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<JournalPost[]>(
    `/api/journal/${entityType}/${entityGuid}/posts${suffix}`,
  );
}

export async function createJournalPost(
  entityType: JournalEntityType,
  entityGuid: string,
  body: CreateJournalPostBody,
): Promise<JournalPost> {
  return apiFetch<JournalPost>(
    `/api/journal/${entityType}/${entityGuid}/posts`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function deleteJournalPost(
  entityType: JournalEntityType,
  entityGuid: string,
  journalPostGuid: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/journal/${entityType}/${entityGuid}/posts/${journalPostGuid}`,
    { method: 'DELETE' },
  );
}
